import dayjs from 'dayjs';
import { Carousel } from 'primereact/carousel';
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { convertToUrlSearchParams } from '../../utils/helper.js';
import BaseButton from '../form/BaseButton.jsx';
import BaseCard from './BaseCard.jsx';
import MeetingAttendeeInfoEdit from './MeetingAttendeeInfoEdit.jsx';
import { useGetParticipantRoleTypesQuery } from '../../api/misc.js';

const AttendeesSummaryCarousel = ({ className, calendarMeetingId, items, noEdit = false }) => {
    const [showOverlay, setShowOverlay] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const navigate = useNavigate();

    const { data: participantRoleTypes } = useGetParticipantRoleTypesQuery({});
    const roleOptions = useMemo(() => {
        if (participantRoleTypes?.response?.participant_role_types) {
            return participantRoleTypes.response.participant_role_types;
        }

        return [];
    }, [participantRoleTypes]);

    const attendeeFollowUp = ({ role, first_name, last_name, email, contact_id }) => {
        const selectedDate = new Date();
        const params = {
            date: dayjs(selectedDate).format('YYYY-MM-DD'),
            follow_up: true,
            role,
            first_name,
            last_name,
            contact_id,
            email
        };

        navigate(`/calendar/add/?${convertToUrlSearchParams(params)}`);
    };

    const handleEditOverlay = (participant) => {
        setSelectedParticipant(participant);
        setShowOverlay(true);
    };

    const participantTemplate = (item) => {
        return (
            <BaseCard className='mt-0 mb-6  shadow-none'>
                {item.meeting_invitation === 'proposed-change' && (
                    <div className='bg-theme1-lightBlue rounded-2xl p-4 mb-4'>
                        <div className='text-sm font-extrabold text-theme1-info'>Proposed Time</div>
                        <div className='text-xs text-theme1-title2 mt-1'>
                            <strong>From </strong>
                            {dayjs(item.proposed_start_date).format('dddd, MMM D, YYYY [at] HH:mm')}
                        </div>
                        <div className='text-xs text-theme1-title2 mt-1'>
                            <strong>To</strong> {dayjs(item.proposed_end_date).format('dddd, MMM D, YYYY [at] HH:mm')}
                        </div>
                    </div>
                )}

                <div className='flex flex-row items-center'>
                    <div className='mr-4'>
                        {item && (
                            <div className='rounded-full w-20 h-20 overflow-hidden'>
                                <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                    <div className='text-theme1-primary font-bold text-3xl'>
                                        {item.contact.avatar ? (
                                            <img src={item.contact.avatar} alt='Avatar' />
                                        ) : (
                                            `${item.contact?.first_name?.substring(0, 1).toUpperCase() || ''}${item.contact?.last_name?.substring(0, 1).toUpperCase() || ''}`
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className='flex-1'>
                        <div>
                            {item.invited && !item.meeting_invitation && (
                                <span
                                    className={`rounded-md px-3 py-1 text-[10px] font-extrabold text-white bg-theme1-lightBtns`}
                                >
                                    Invited
                                </span>
                            )}
                            {item.meeting_invitation && (
                                <span
                                    className={`rounded-md px-3 py-1 text-[10px] font-extrabold text-white ${
                                        item.meeting_invitation === 'accepted'
                                            ? 'bg-theme1-success'
                                            : item.meeting_invitation === 'declined'
                                              ? 'bg-theme1-error'
                                              : item.meeting_invitation === 'proposed-change'
                                                ? 'bg-theme1-info'
                                                : item.meeting_invitation === 'update-needed'
                                                  ? 'bg-theme1-warmGreen'
                                                  : ''
                                    }`}
                                >
                                    {item.meeting_invitation === 'proposed-change'
                                        ? 'Propose New Time'
                                        : item.meeting_invitation.charAt(0).toUpperCase() +
                                          item.meeting_invitation.slice(1).replace('-', ' ')}
                                </span>
                            )}
                        </div>
                        <div className='flex items-center mt-1'>
                            {item && (
                                <div className='text-theme1-primary font-bold text-2xl'>
                                    <Link to={`/contacts/view/${item.contact_id}`}>
                                        {item.contact?.first_name} {item.contact?.last_name}
                                    </Link>
                                </div>
                            )}
                        </div>
                        {item && item.role && (
                            <div className='text-sm text-theme1-title2 font-bold mt-1'>
                                {roleOptions.find((role) => role.value === item.role)?.label}
                            </div>
                        )}
                        {item && item.contact?.companies?.length > 0 && (
                            <div className='text-sm text-theme1-primary2 font-bold mt-1 opacity-50'>
                                {item.contact?.companies?.[0].job_title}
                            </div>
                        )}
                        {item && item.contact?.companies?.length > 0 && (
                            <div className='mt-1 text-base font-bold text-theme1-text2'>
                                {item.contact?.companies?.[0].name}
                            </div>
                        )}
                        <div className='flex items-center mt-3 gap-[10px] flex-wrap'>
                            <BaseButton
                                className='bg-theme1-primary p-2 px-3 sm:px-5 text-white text-sm sm:text-base w-auto flex-shrink-0'
                                textValue='View info'
                                onClick={() => handleEditOverlay(item)}
                            />
                            {!noEdit && (
                                <BaseButton
                                    className='bg-theme1-primary p-2 px-3 sm:px-5 text-white text-sm sm:text-base w-auto flex-shrink-0'
                                    textValue='Follow up'
                                    onClick={() =>
                                        attendeeFollowUp({
                                            role: item.role,
                                            first_name: item.contact?.first_name,
                                            last_name: item.contact?.last_name,
                                            email: item.email,
                                            contact_id: item.contact_id
                                        })
                                    }
                                />
                            )}
                        </div>
                    </div>
                </div>
            </BaseCard>
        );
    };

    return (
        <div className={twMerge('', className)}>
            <Carousel
                value={items}
                numScroll={1}
                numVisible={1}
                itemTemplate={participantTemplate}
                showNavigators={false}
                circular={false}
                showIndicators
                page={0}
            />

            {selectedParticipant && (
                <MeetingAttendeeInfoEdit
                    calendarMeetingId={calendarMeetingId}
                    attendeeId={selectedParticipant.participant_id}
                    attendeeCustomInfo={selectedParticipant.info}
                    attendeeAiInfo={selectedParticipant.openai_info}
                    showOverlay={showOverlay}
                    setShowOverlay={setShowOverlay}
                    noEdit={noEdit}
                />
            )}
        </div>
    );
};

export default AttendeesSummaryCarousel;
